import React, { useState, useEffect } from 'react';
import ReactECharts from 'echarts-for-react';

import { commonOptions } from 'data/eco-environmental-survey';
import { GRAPH_HEIGHT, GRAPH_COLOR } from 'data/common';

function BarGraph(props) {
  const { scale, title, unit, target, allData, select } = props;
  const { main, sub } = target[scale];
  const graphYear = select[scale].year;

  const [xAxis, setXAxis] = useState([]);
  const [series, setSeries] = useState({ main: [], sub: [] });
  const [mainData, setMainData] = useState(null); // null | object
  const [subData, setSubData] = useState(null); // null | object

  const height = GRAPH_HEIGHT.default;
  const isNoSub = !sub || sub === '';
  const isFetchingData = allData.length === 0;

  const handleKey = (key, result) => {
    switch (key) {
      case '縣市代碼':
      case '縣市名稱':
      case '鄉鎮市區代碼':
      case '鄉鎮市區名稱':
      case '資料時間':
        return '-';
      default:
        return result;
    }
  };

  useEffect(() => {
    if (isFetchingData) return;

    const yearItem = allData.find(
      (item) => String(item.year) === String(graphYear)
    );

    if (!yearItem || !Array.isArray(yearItem.data)) {
      // 該年份沒有資料 → 清空
      setMainData(null);
      setSubData(null);
      setXAxis([]);
      setSeries({ main: [], sub: [] });
      return;
    }

    const mainRec =
      yearItem.data.find((v) => v['鄉鎮市區名稱'] === target?.[scale]?.main) ||
      null;

    const subRec = !isNoSub
      ? yearItem.data.find((v) => v['鄉鎮市區名稱'] === target?.[scale]?.sub) ||
        null
      : null;

    setMainData(mainRec);
    setSubData(subRec);
  }, [isFetchingData, allData, graphYear, scale, target, isNoSub]);

  // 根據 mainData 產出 x 軸與 series
  useEffect(() => {
    if (!mainData) {
      setXAxis([]);
      setSeries((prev) => ({ ...prev, main: [] }));
      return;
    }

    const entries = Object.entries(mainData);
    const x = entries
      .map(([key]) => handleKey(key, key))
      .filter((k) => k !== '-');

    const mainSeries = entries
      .map(([key, value]) => handleKey(key, value))
      .filter((v) => v !== '-');

    setXAxis(x);
    setSeries((prev) => ({ ...prev, main: mainSeries }));
  }, [mainData]);

  // 根據 subData 產出 series
  useEffect(() => {
    if (!subData) {
      setSeries((prev) => ({ ...prev, sub: [] }));
      return;
    }

    const subSeries = Object.entries(subData)
      .map(([key, value]) => handleKey(key, value))
      .filter((v) => v !== '-');

    setSeries((prev) => ({ ...prev, sub: subSeries }));
  }, [subData]);

  const option = {
    ...commonOptions,
    color: GRAPH_COLOR.legend.double,
    title: { text: `${graphYear}年${title}` },
    dataZoom: { show: true },
    tooltip: {
      trigger: 'axis',
      backgroundColor: GRAPH_COLOR.tooltip.background.dark,
      axisPointer: { type: 'shadow', label: { show: true } },
    },
    legend: {
      top: 'auto',
      left: 'center',
      bottom: '12.5%',
      itemGap: 5,
    },
    grid: { width: '90%', left: '15%', height: '60%' },
    xAxis: { type: 'category', boundaryGap: true, data: xAxis },
    yAxis: { name: `(${unit})`, type: 'value' },
    series: [
      { data: series.main, type: 'bar', name: main || '主' },
      ...(!isNoSub
        ? [{ data: series.sub, type: 'bar', name: sub || '次' }]
        : []),
    ],
  };

  // 無資料時的訊息（主、次分開判斷）
  const noDataMessage =
    !isFetchingData && !mainData
      ? `缺少 ${graphYear} 年的資料`
      : !isFetchingData && !isNoSub && !subData
      ? `缺少 ${graphYear} 年的資料`
      : null;

  return (
    <div className="col-12 col-md-6">
      {noDataMessage ? (
        <div
          className="d-flex align-items-center justify-content-center text-muted"
          style={{ height }}
        >
          {noDataMessage}
        </div>
      ) : (
        <ReactECharts
          option={option}
          notMerge={true}
          lazyUpdate={true}
          opts={{ renderer: 'canvas', height: `${height}` }}
          style={{ height }}
        />
      )}
    </div>
  );
}

export default BarGraph;
